import yaml from 'js-yaml';
import ContentService from 'services/loader-handler-services/content-service';
import CourseSettingsService from 'services/course-handler-services/course-settings-service';
import LocaleService from 'services/loader-handler-services/locale-service';
import AsyncComponentService from 'services/loader-handler-services/async-component-service';
import HtmlImportService from 'services/loader-handler-services/html-import-service';
import Chaplin from 'chaplin';
import Backbone from 'backbone';
import BlocksView from 'views/block/block-collection-view';
import { cloneDeep } from 'lodash';
import DOMPurify from 'dompurify';

// the dialect has no div: at this point every div is a view container or a region placeholder
const SCAFFOLDING_SELECTOR = 'div';

const INLINE_TAGS = ['strong', 'em', 'b', 'i', 'u', 's', 'sup', 'sub', 'br', 'a'];
const IMPORT_TAGS = INLINE_TAGS.concat(['p', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'table', 'thead', 'tbody', 'tr', 'th', 'td']);
const IMPORT_ATTRS = ['href', 'colspan', 'rowspan'];

/**
 * Reduce an imported HTML document (legal text and the like) to the export dialect: its headings become
 * in-page headers, loose text becomes paragraphs, and everything outside the dialect is unwrapped or dropped.
 * @param {string} html the fetched document
 * @returns dialect HTML
 */
function sanitizeImportedHtml(html) {
  const root = document.createElement('div');
  root.innerHTML = DOMPurify.sanitize(html, { ALLOWED_TAGS: IMPORT_TAGS, ALLOWED_ATTR: IMPORT_ATTRS });
  // an imported document starts its own hierarchy; inside a page only h5 and h6 exist
  root.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(heading => {
    const demoted = document.createElement(Number(heading.tagName[1]) <= 3 ? 'h5' : 'h6');
    demoted.innerHTML = heading.innerHTML;
    heading.replaceWith(demoted);
  });
  // legal documents put text, emphasis and nested lists straight inside <ol>/<ul>; a list may only hold items
  root.querySelectorAll('ul, ol').forEach(list => {
    let item = null;
    Array.from(list.childNodes).forEach(node => {
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'LI') {
        item = node;
      } else if (node.nodeType === Node.TEXT_NODE && !node.textContent.trim()) {
        node.remove();
      } else {
        if (!item) {
          item = list.insertBefore(document.createElement('li'), node);
        }
        item.appendChild(node);
      }
    });
  });
  const output = document.createElement('div');
  let paragraph = null;
  Array.from(root.childNodes).forEach(node => {
    if (node.nodeType === Node.ELEMENT_NODE && !INLINE_TAGS.includes(node.tagName.toLowerCase())) {
      paragraph = null;
      output.appendChild(node);
    } else if (paragraph || node.nodeType !== Node.TEXT_NODE || node.textContent.trim()) {
      paragraph = paragraph ?? output.appendChild(document.createElement('p'));
      paragraph.appendChild(node);
    }
  });
  Array.from(output.children).forEach(child => {
    if (child.tagName === 'P' && !child.textContent.trim() && !child.querySelector('br')) {
      child.remove();
    }
  });
  return output.innerHTML;
}

export default class BlockExporter {
  static sanitizeImportedHtml(html) {
    return sanitizeImportedHtml(html);
  }

  /**
   * @param {Object} options
   * @param {string} options.mode 'authoring' (default) keeps today's annotated document; 'transcript' emits the bare article
   */
  constructor({ mode } = {}) {
    this.mode = mode === 'transcript' ? 'transcript' : 'authoring';
    this.authoring = this.mode === 'authoring';
    this.exportLevel = 'course';
    this.exportPages = [];
    this.exportIndex = 0;
    this.exportHtml = '';
    this.exportView = null;
  }
  /**
   * getCourseLevel
   * @param {*} exportLevel string representing the level of the course to export; alternatively, a dot-delimited string representing a specific page to export
   * @returns an array of export blocks representing the course
   */
  getCourseLevel(exportLevel) {
    if (exportLevel === 'course') {
      // the navigation dump is an authoring aid; a transcript has the table of contents instead
      const navigation = this.authoring ? this.getNavigation() : [];
      return navigation.concat(this.getCourseToc('pages,menus')).concat(this.getCoursePages()).concat(this.getCourseMenus());
    } else if (exportLevel === 'menus') {
      return this.getCourseToc('menus').concat(this.getCourseMenus());
    } else if (exportLevel === 'navigation') {
      return this.getNavigation();
    } else if (exportLevel === 'course_pages') {
      return this.getCourseToc('pages').concat(this.getCoursePages(false));
    } else {
      return this.getPage(exportLevel);
    }
  }

  /**
   * getNavigation
   * @returns an array of export blocks representing the course navigation
   */
  getNavigation() {
    return [{
      keys: ["navigation"],
      blocks: [
        {
          type: "export",
          exportTemplate: "structure",
          title: "Navigation",
        }
      ]
    },
    {
      keys: ["modules"],
      blocks: [
        {
          type: "export",
          exportTemplate: "container",
          blocks: cloneDeep(ContentService.moduleContent.modules.blocks),
        }
      ],
    },
    ]
  }

  /**
   * getCourseMenus
   * @returns an array of export blocks representing the course menus
   */
  getCourseMenus() {
    return Object.entries(cloneDeep(ContentService.moduleContent.menu)).reduce((acc, [key, value]) => {
      acc.push({
        keys: ["menus", key],
        blocks: [
          {
            type: "export",
            exportTemplate: "page",
            title: value.title,
            structureKeys: ["menus", key],
            blocks: value.blocks,
            orphanLocales: this.getOrphanLocales(`menus.${key}`, value.blocks),
          }
        ],
      });
      return acc;
    }, [{
      keys: ["menus"],
      blocks: [
        {
          type: "export",
          exportTemplate: "structure",
          title: "Menus",
        }
      ]
    }]);
  }

  /**
   * getPage
   * @param {*} keys dot-delimited string representing a specific page to export
   * @returns an array of export blocks representing a single page
   */
  getPage(keys) {
    const [moduleKey, activityKey, pageKey] = keys.split('.');
    if (!moduleKey || !activityKey || !pageKey) {
      return [];
    }
    const page = cloneDeep(ContentService.moduleContent[moduleKey][activityKey][pageKey]);
    return [{
      keys: [moduleKey, activityKey, pageKey],
      blocks: [
        {
          type: "export",
          exportTemplate: "page",
          title: page.title,
          structureKeys: [moduleKey, activityKey, pageKey],
          blocks: page.blocks,
          orphanLocales: this.getOrphanLocales(`${moduleKey}.${activityKey}.${pageKey}`, page.blocks),
          skipLogic: page.skip ? JSON.stringify(page.skip, null, 2) : null,
        }
      ],
    }];
  }

  /**
   * getOrphanLocales
   * @param {*} namespace i118next namespace
   * @param {*} blocks raw block data
   * @returns an array of strings representing orphan locales
   */
  getOrphanLocales(namespace, blocks) {
    LocaleService.addDefaultResources(namespace, false);
    const allLocales = LocaleService.getResourceByNamespace(namespace) ?? {};
    const foundLocales = this.traceLocaleKeys(blocks, allLocales, []);
    const orphanLocales = Object.keys(allLocales).filter(key => { return !foundLocales.includes(key) });
    return orphanLocales.map(key => { return `${key}: ${LocaleService.t(key)}` });
  }

  /**
   * getCourseToc
   * @param {*} contentTypes string indicating whether to include menus and/or pages in the table of contents
   * @returns an array of export blocks representing the course table of contents
   */
  getCourseToc(contentTypes) {
    return [{
      keys: ["toc"],
      blocks: [
        {
          type: "export",
          exportTemplate: "toc",
          course: CourseSettingsService.get('name'),
          modules: contentTypes.indexOf('pages') >= 0 ? ContentService.modules.reduce((acc_m, module) => {
            const a = module.activities.reduce((acc_a, activity) => {
              const p = activity.pages.reduce((acc_p, page) => {
                acc_p.push({
                  keys: [module.key, activity.key, page.key],
                  title: page.title,
                });
                return acc_p;
              }, []);
              acc_a.push({
                keys: [module.key, activity.key],
                title: activity.title,
                pages: p,
              });
              return acc_a;
            }, []);
            acc_m.push({
              keys: [module.key],
              title: module.title,
              activities: a,
            });
            return acc_m;
          }, []) : [],
          menus: contentTypes.indexOf('menus') >= 0 ? Object.keys(ContentService.moduleContent.menu).reduce((acc, key) => {
            acc.push({
              keys: ["menus", key],
              title: key,
            });
            return acc;
          }, []) : [],
        }
      ],
    }];
  }

  /**
   * getCoursePages
   * @returns an array of export blocks representing every page in the course
   */
  getCoursePages() {
    return ContentService.modules.reduce((acc_m, module) => {
      const a = module.activities.reduce((acc_a, activity) => {
        const p = activity.pages.reduce((acc_p, page) => {
          const rawblocks = cloneDeep(ContentService.moduleContent[module.key][activity.key][page.key].blocks);
          const skipLogic = cloneDeep(ContentService.moduleContent[module.key][activity.key][page.key].skip);
          acc_p.push({
            keys: [module.key, activity.key, page.key],
            blocks: [
              {
                type: "export",
                exportTemplate: "page",
                title: page.title,
                structureKeys: [module.key, activity.key, page.key],
                blocks: rawblocks,
                orphanLocales: this.getOrphanLocales(`${module.key}.${activity.key}.${page.key}`, rawblocks),
                skipLogic: skipLogic ? JSON.stringify(skipLogic, null, 2) : null,
              }
            ],
          });
          return acc_p;
        }, []);
        acc_a.push({
          keys: [module.key, activity.key],
          blocks: [
            {
              type: "export",
              exportTemplate: "structure",
              level: "activity",
              title: activity.title,
              description: activity.description,
              structureKeys: [module.key, activity.key],
            }
          ]
        });
        return acc_a.concat(p);
      }, []);
      acc_m.push({
        keys: [module.key],
        blocks: [
          {
            type: "export",
            exportTemplate: "structure",
            level: "module",
            title: module.title,
            description: module.description,
            structureKeys: [module.key],
          }
        ]
      });
      return acc_m.concat(a);
    }, []);
  }

  /**
   * traceLocaleKeys
   * @param {*} node raw block data
   * @param {*} namespaceLocales object containing locale data for a namespace (e.g., page)
   * @param {*} foundLocales empty array to store found locales
   * @returns array containing locale keys from the namespace that were found in the block data
   */
  traceLocaleKeys(node, namespaceLocales, foundLocales) {
    for (const key in node) {
      if (Object.prototype.hasOwnProperty.call(node, key)) {
        if (typeof node[key] === 'string') {
          if (Object.prototype.hasOwnProperty.call(namespaceLocales, node[key])) {
            foundLocales.push(`${node[key]}`);
          }
        } else if (typeof node[key] === 'object' && node[key] !== null) {
          this.traceLocaleKeys(node[key], namespaceLocales, foundLocales);
        }
      }
    }
    return foundLocales;
  }

  /**
   * Convert all blocks in an array to export blocks
   * @param {*} blockArray This is raw block data
   * @returns an array of export blocks, each stamped with `authoring` so templates can read `@root.authoring`
   */
  async convertBlockArray(blockArray) {
    return (
      await Promise.all(blockArray.map(
        async block => {
          const exportBlock = await this.convertBlock(block);
          if (exportBlock) {
            exportBlock.authoring = this.authoring;
          }
          return exportBlock;
        }
      ))
    )
  }

  /**
   * Convert one block to an export block
   * @param {*} block This is raw block data
   * @returns an export block, or undefined for a component that cannot be loaded
   */
  async convertBlock(block) {
    if (block.type === 'component') {
      let template;
      const sanitizedPath = DOMPurify.sanitize(block.path);
      try {
        template = block.path ? require(`components/${sanitizedPath}`) : null;
      } catch (e) {
        [template] = await AsyncComponentService.load([sanitizedPath]);
      }
      if (template) {
        const data = this.parseJsonYaml(block, template);
        return {
          type: 'export',
          exportTemplate: 'component',
          class: `parsed-component-${sanitizedPath}`,
          path: sanitizedPath,
          blocks: await this.convertBlockArray(data.blocks),
        }
      }
    } else if (block.type === 'switch') {
      return {
        type: 'export',
        exportTemplate: 'switch',
        source: block.source,
        defaultBlocks: {
          blocks: await this.convertBlockArray(block.defaultBlocks?.blocks || [])
        },
        values: await Object.entries(block).reduce(async (accPromise, [key, value]) => {
          const acc = await accPromise;
          if (key !== "defaultBlocks" && typeof value === 'object' && value !== null && value.blocks) {
            acc.push({
              value: key,
              blocks: await this.convertBlockArray(value.blocks),
            });
          }
          return acc;
        }, Promise.resolve([])),
      }
    } else if (block.type === 'html-import') {
      return {
        ...block,
        type: 'export',
        exportTemplate: 'html-import',
        importUrl: HtmlImportService.parsePath({ path: block.path, url: block.url }),
        ...(await this.importHtml(block)),
      };
    } else if (block.type !== 'export') {
      const convertedChildBlocks = await this.convertChildBlocks(block);
      return {
        ...convertedChildBlocks,
        type: 'export',
        exportTemplate: block.type
      };
    }
    return await this.convertChildBlocks(block);
  }

  /**
   * Convert all child blocks in an object to export blocks
   * @param {*} dataNode This is an arbitrary chunk of raw block data
   * @returns an object with export blocks
   */
  async convertChildBlocks(dataNode) {
    for (const key in dataNode) {
      if (Array.isArray(dataNode[key]) && (key === 'blocks' || key === 'fields')) {
        // eslint-disable-next-line require-atomic-updates
        dataNode[key] = await this.convertBlockArray(dataNode[key]);
      } else if (typeof dataNode[key] === 'object' && dataNode[key] !== null) {
        await this.convertChildBlocks(dataNode[key]);
      }
    }
    return dataNode;
  }

  /**
   * Fetch the document an html-import block shows, the way the block does at run time: the path (or url) first,
   * the default as a fallback.
   * @param {Object} block raw html-import block
   * @returns {Promise<{importedHtml: string, importError?: string}>} dialect HTML, empty with a reason when nothing could be fetched
   */
  async importHtml(block) {
    const candidates = [{ path: block.path, url: block.url }];
    if (block.default) {
      candidates.push({ path: block.path, url: block.default });
    }
    let importError = 'nothing to fetch';
    for (const candidate of candidates) {
      const url = HtmlImportService.parsePath(candidate);
      try {
        const response = await fetch(url);
        if (response.status === 200) {
          return { importedHtml: sanitizeImportedHtml(await response.text()) };
        }
        importError = `${url} answered ${response.status}`;
      } catch (error) {
        importError = `${url} could not be fetched`;
      }
    }
    return { importedHtml: '', importError };
  }

  /**
   * Parse component parameters and return blockdata based on the component template
   * @param {Object} block This is the path to the component template
   * @param {Object} componentTemplate This is a handlebars template
   * @returns an object containing a block array
   */
  parseJsonYaml(block, componentTemplate) {
    const parameters = block.parameters ?? {};
    try {
      try {
        return JSON.parse(componentTemplate(parameters));
      } catch (e) {
        return yaml.load(componentTemplate(parameters));
      }
    } catch (e) {
      console.error(`component-view:  ${DOMPurify.sanitize(block.path)} not parsable`, e); //eslint-disable-line no-console
    }
    return { blocks: [] };
  }

  exportBlocks(exportLevel, exportView) {
    this.exportLevel = exportLevel;
    this.exportIndex = 0;
    this.exportHtml = '';
    this.exportView = exportView;
    try {
      this.exportPages = this.getCourseLevel(exportLevel);
    } catch (error) {
      this.fail(error);
      return;
    }
    this.exportPage();
  }

  /**
   * Report an export that cannot finish, so the requester can clear its state
   * @param {*} error what went wrong
   */
  fail(error) {
    this.exportView.trigger('block:export:failed', error);
  }

  /**
   * @returns metadata describing this export, posted back to Forge alongside the content
   */
  exportMeta() {
    return {
      course: CourseSettingsService.get('name'),
      locale: LocaleService.getLanguage(),
      scope: this.exportLevel,
      mode: this.mode,
      generatedAt: new Date().toISOString(),
    };
  }

  exportDocument() {
    const content = this.authoring ? this.authoringDocument() : this.transcriptDocument();
    this.exportView.trigger('block:export:completed', content, this.exportMeta());
  }

  /**
   * @returns the course content as a bare article: every div (view containers, state wrappers, region placeholders) is unwrapped so only template output remains
   */
  transcriptDocument() {
    const root = document.createElement('div');
    root.innerHTML = this.exportHtml;
    let scaffolding = root.querySelector(SCAFFOLDING_SELECTOR);
    while (scaffolding) {
      scaffolding.replaceWith(...scaffolding.childNodes);
      scaffolding = root.querySelector(SCAFFOLDING_SELECTOR);
    }
    this.dropRepeatedHeadings(root);
    // containers, layouts and buttons that held nothing exportable leave empty sections behind
    let emptySection = this.findEmptySection(root);
    while (emptySection) {
      emptySection.remove();
      emptySection = this.findEmptySection(root);
    }
    this.dropEmptyPages(root);
    const meta = this.exportMeta();
    const article = document.createElement('article');
    article.dataset.export = 'course';
    article.dataset.course = meta.course ?? '';
    article.dataset.locale = meta.locale ?? '';
    article.dataset.mode = this.mode;
    article.append(...root.childNodes);
    return article.outerHTML;
  }

  /**
   * @param {Element} root detached element holding the transcript
   * @returns the first section with no text and no media or table, or null
   */
  findEmptySection(root) {
    return Array.from(root.querySelectorAll('section')).find(
      section => !section.textContent.trim() && !section.querySelector('figure, table')
    ) ?? null;
  }

  /**
   * Pages, tabs and accordion bellows usually open with a header block repeating the title just printed;
   * the transcript keeps each title once.
   * @param {Element} root detached element holding the transcript
   */
  dropRepeatedHeadings(root) {
    const normalise = text => text.replace(/\s+/g, ' ').trim().toLowerCase();
    root.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(title => {
      // a heading removed earlier in this pass (the root is detached, so isConnected cannot tell)
      if (!root.contains(title)) {
        return;
      }
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      walker.currentNode = title;
      let node = walker.nextNode();
      while (node && (title.contains(node) || !node.textContent.trim())) {
        node = walker.nextNode();
      }
      const heading = node?.parentElement?.closest('h1, h2, h3, h4, h5, h6');
      if (heading && heading !== title && normalise(heading.textContent) === normalise(title.textContent)) {
        heading.remove();
      }
    });
  }

  /**
   * A page whose blocks exported nothing (customization placeholders, platform-sourced media) is left out,
   * together with its table of contents entry.
   * @param {Element} root detached element holding the transcript
   */
  dropEmptyPages(root) {
    root.querySelectorAll('h4').forEach(pageTitle => {
      const content = [];
      let sibling = pageTitle.nextElementSibling;
      while (sibling && !/^H[1-4]$/.test(sibling.tagName)) {
        content.push(sibling);
        sibling = sibling.nextElementSibling;
      }
      const hasContent = content.some(
        element => element.textContent.trim() || element.matches('figure, table') || element.querySelector('figure, table')
      );
      if (hasContent) {
        return;
      }
      content.forEach(element => element.remove());
      if (pageTitle.id) {
        root.querySelectorAll(`nav a[href="#${pageTitle.id}"]`).forEach(link => {
          const item = link.closest('li');
          const list = item?.parentElement;
          item?.remove();
          if (list && list.matches('ol, ul') && !list.children.length) {
            list.remove();
          }
        });
      }
      pageTitle.remove();
    });
  }

  /**
   * @returns the annotated engineer-facing document with its inline stylesheet
   */
  authoringDocument() {
    return `<!DOCTYPE html PUBLIC "-//W3C//DTD HTML 4.01//EN" "http://www.w3.org/TR/html4/strict.dtd">
      <html><head>
        <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
        <meta http-equiv="Content-Style-Type" content="text/css">
        <meta name="title" content="Export of ${CourseSettingsService.get('name')}">
        <meta name="description" content="Exported ${this.exportLevel} on ${(new Date()).toLocaleDateString()}">
        <meta name="author" content="EVERFI">
        <style>
          .toc-link {
            text-align: right;
          }
          .tooltip {
            position: relative;
            display: inline-block;
            color: black;
          }
          .tooltip .tooltiptext {
            visibility: hidden;
            min-width: 200px;
            width: auto;
            background-color: white;
            color: black;
            text-align: center;
            padding: 8px;
            border-radius: 6px;
            border: 1px solid black;
            position: absolute;
            z-index: 1;
          }
          .tooltip:hover .tooltiptext {
            visibility: visible;
          }
          pre.skip-logic {
            text-align: left;
            color: lightgray;
          }
          .structure-export-metadata {
            display: flex;
            justify-content: flex-end;
            background-color: powderblue;
            width: 100%;
            margin-left: -10px;
            padding-right: 10px;
          }
          .page-export-metadata {
            display: flex;
            justify-content: flex-end;
            background-color: powderblue;
            width: 100%;
            margin-left: -10px;
            padding-right: 10px;
          }
          .page-export-content {margin: 10px;}
          .default-export-template {
            background-color: rgb(236 247 255);
            .default-export-metadata {
              display: flex;
              justify-content: flex-end;
              color: #aaaaaa;
            }
          }
          .parsed-component-metadata {
            display: flex;
            justify-content: flex-end;
            width: 100%;
          }
          .parsed-component {
            border-top: 1px solid powderblue;
            border-left: 3px solid powderblue;
            padding-top: 5px;
            margin-top: 5px;
            margin-left: 5px;
          }
          .parsed-block-metadata {
            display: flex;
            justify-content: flex-end;
            width: 100%;
          }
          .parsed-block {
            border-top: 1px solid powderblue;
            border-left: 3px solid powderblue;
            padding-top: 5px;
            margin-top: 5px;
            margin-left: 5px;
          }
          .text-diminished {
            color: lightgray;
          }
          .button-metadata {
            float: right;
          }
          .button-content {
            background-color: powderblue;
            border: none;
            border-radius: 4px;
            color: black;
            padding: 5px 32px;
            text-align: center;
            text-decoration: none;
            display: inline-block;
            font-size: 16px;
            margin: 4px 2px;
          }
          .screen-reader-content {
            color: lightgray;
          }
          .orphaned-locales {
            border-collapse: collapse;
            table, td {
              border: 1px solid lightgray;
              border-collapse: collapse;
            }
            td {
              padding-left: 5px;
              padding-right: 5px;
              color: lightgray
            }
          }
          .table-export {
            border-collapse: collapse;
            table, th, td {
              border: 1px solid;
              border-collapse: collapse;
            }
            th, td {
              padding-left: 5px;
              padding-right: 5px;
            }
          }
        </style>
        <title>Export ${CourseSettingsService.get('name')}</title>
      </head><body>
      <div>Generated on ${(new Date()).toLocaleDateString()}</div>
      <hr>
      <div>${this.exportHtml}</div>
      </body></html>`;
  }

  async exportPage() {
    try {
      if (this.exportPages.length <= this.exportIndex) {
        this.exportDocument();
        return;
      }

      const page = this.exportPages[this.exportIndex];
      const pageBlocks = page.blocks;

      if (!Array.isArray(pageBlocks) || pageBlocks.length < 1) {
        this.exportNextPage(); // skip empty pages
        return;
      }
      // make a view for each page to export
      const pageLocalizedView = new BlocksView({
        collection: new Chaplin.Collection({
          type: 'container',
          blocks: await this.convertBlockArray(pageBlocks),
        }),
        pageView: this,
        parent: this,
      });
      // update the locales namespace
      LocaleService.addDefaultResources(page.keys.join('.'), false);
      // the first loaded event means every nested region has loaded; later ones are nested blocks re-announcing
      this.exportView.listenToOnce(pageLocalizedView, 'block:state:loaded', () => {
        try {
          this.exportHtml += pageLocalizedView.el.innerHTML;
          pageLocalizedView.dispose();
        } catch (error) {
          this.fail(error);
          return;
        }
        this.exportNextPage();
      });

      pageLocalizedView.trigger('addedToDOM');
    } catch (error) {
      this.fail(error);
    }
  }

  exportNextPage() {
    this.exportIndex++;
    this.exportPage();
  }
}

// the page views listen to their parent for page state, so the exporter must speak Backbone events
Object.assign(BlockExporter.prototype, Backbone.Events);
